import { supabase } from '../supabase';
import type { ClientContact } from '../types';
import { fetchInChunks } from './inChunks';

/**
 * Contatto-referente in forma "legacy-compatibile" ({ id, email, name }),
 * così i consumer che prima leggevano `client_contacts` non cambiano a valle.
 */
export interface ReferenteContact {
  id: string;
  email: string;
  name: string | null;
}

/**
 * Legge i referenti (persone via client_relationships('contatto', is_active)) di una o
 * più aziende-cliente e li mappa nella shape `ClientContact` legacy, così il READ del
 * cliente è SIMMETRICO al WRITE (persistClientReferenti scrive su client_relationships).
 *
 * Senza questo, un cliente creato col nuovo modello ha `client_contacts` vuoto: il form
 * di modifica mostrerebbe zero contatti e, al salvataggio, il diff soft-unlink di
 * updateClient scollegherebbe TUTTI i referenti (perdita dati). Vedi §14 spec.
 *
 * Ritorna una mappa clientId(azienda) → ClientContact[] (usabile sia per il fetch
 * singolo che per il bulk). `id` = id della relazione (stabile, univoco per link).
 */
export async function fetchReferentiAsContacts(
  companyClientIds: string[],
): Promise<Record<string, ClientContact[]>> {
  const ids = Array.from(new Set((companyClientIds || []).filter(Boolean)));
  const out: Record<string, ClientContact[]> = {};
  if (ids.length === 0) return out;

  let data: any[];
  try {
    data = await fetchInChunks<any>(ids, chunk =>
      supabase
        .from('client_relationships')
        .select(`
      id, target_client_id, organization_id, role, area, notes, created_at, updated_at,
      is_referente, is_rappresentante_legale, is_primary_contact,
      person:source_client_id ( id, business_name, email, phone, birth_date, newsletter_subscribed )
    `)
        .eq('relationship_type', 'contatto')
        .eq('is_active', true)
        .in('target_client_id', chunk)
    );
  } catch (error) {
    console.error('[fetchReferentiAsContacts] error:', error);
    return out;
  }

  for (const row of data || []) {
    const r: any = row;
    const p: any = r.person;
    if (!p) continue;
    const companyId = r.target_client_id as string;
    (out[companyId] ||= []).push({
      id: r.id,
      client_id: companyId,
      organization_id: r.organization_id,
      person_id: p.id,
      name: p.business_name || p.email || '',
      phone: p.phone || undefined,
      email: p.email || undefined,
      role: r.role || undefined,
      area: (r.area || 'altro') as ClientContact['area'],
      notes: r.notes || undefined,
      is_primary: r.is_primary_contact ?? false,
      is_active: true,
      is_referente: r.is_referente ?? false,
      is_rappresentante_legale: r.is_rappresentante_legale ?? false,
      birth_date: p.birth_date || undefined,
      newsletter_subscribed: p.newsletter_subscribed ?? false,
      created_at: r.created_at || '',
      updated_at: r.updated_at || '',
    });
  }
  return out;
}

/**
 * Fonde i contatti legacy (client_contacts) con i referenti nuovi (client_relationships),
 * dedup per email (preferendo la versione da client_relationships). I contatti senza email
 * restano entrambi (non deduplicabili in modo affidabile).
 */
export function mergeContactsByEmail(
  legacy: ClientContact[],
  referenti: ClientContact[],
): ClientContact[] {
  // Dedup per persona: un legacy già mappato (person_client_id) a una persona
  // presente tra i referenti è la STESSA entità → tieni solo la versione nuova.
  // Indispensabile per i contatti senza email, altrimenti non deduplicabili.
  const referentiPersonIds = new Set(
    (referenti || []).map(c => c.person_id).filter(Boolean) as string[],
  );
  const legacyFiltered = (legacy || []).filter(
    c => !c.person_client_id || !referentiPersonIds.has(c.person_client_id),
  );

  const byEmail = new Map<string, ClientContact>();
  const noEmail: ClientContact[] = [];
  const add = (c: ClientContact, override: boolean) => {
    const key = (c.email || '').trim().toLowerCase();
    if (!key) { noEmail.push(c); return; }
    if (override || !byEmail.has(key)) byEmail.set(key, c);
  };
  for (const c of legacyFiltered) add(c, false);
  for (const c of referenti || []) add(c, true); // client_relationships = fonte di verità
  return [...byEmail.values(), ...noEmail];
}

/**
 * Ritorna i referenti (persone) collegati a un'azienda-cliente via
 * client_relationships('contatto', is_active), con email valorizzata.
 * Sostituisce le vecchie letture di `client_contacts` per popolare gli
 * attendee delle attività (videochiamata/appuntamento/sopralluogo).
 *
 * Dedup per persona (una persona può essere referente di più aziende, ma qui
 * filtriamo per una singola azienda target quindi i doppioni sono improbabili;
 * la Set garantisce comunque unicità).
 */
export async function fetchReferentiContacts(companyClientId: string): Promise<ReferenteContact[]> {
  if (!companyClientId) return [];
  const { data, error } = await supabase
    .from('client_relationships')
    .select('person:source_client_id ( id, business_name, email )')
    .eq('relationship_type', 'contatto')
    .eq('is_active', true)
    .eq('target_client_id', companyClientId);
  if (error) {
    console.error('[fetchReferentiContacts] error:', error);
    return [];
  }
  const seen = new Set<string>();
  const out: ReferenteContact[] = [];
  for (const row of data || []) {
    const p: any = (row as any).person;
    if (!p || !p.email || seen.has(p.id)) continue;
    seen.add(p.id);
    out.push({ id: p.id, email: p.email, name: p.business_name });
  }
  return out;
}
